import { TranslatePipe } from '@ngx-translate/core';

import { Splitter, SplitterResizeEndEvent } from 'primeng/splitter';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  EventListComponent,
  EventListOptions
} from '../../../shared/event-list/event-list.component';
import { SchedulerEvent } from '../../../../models/SchedulerEvent';
import { ScheduleService } from '../../../../services/schedule.service';
import { SearchCriteria } from '../../../../models/SearchCriteria';
import { ButtonDirective } from 'primeng/button';
import { PrimeTemplate } from 'primeng/api';
import { HealthRecordService } from '../../../../services/healthrecord.service';
import { CustomerService } from '../../../../services/customer.service';
import { Router } from '@angular/router';
import { ExpiringComponent } from '../expiring/expiring.component';
import { SettingService } from '../../../../services/setting.service';

import { AppDatePipe } from '../../../../pipes/app-date.pipe';
/**
 * Retrieves and shows the events of the selected user for a specific day.
 * Events are separated to appoinments and todos list.
 * User can select the day.
 */
@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss'],

  imports: [
    TranslatePipe,
    AppDatePipe,
    Splitter,
    EventListComponent,
    ButtonDirective,
    PrimeTemplate,
    ExpiringComponent
  ]
})
export class EventsComponent implements OnInit, OnChanges, OnDestroy {
  public appointmentListOptions: EventListOptions;
  public todoListOptions: EventListOptions;
  public appointments: Array<SchedulerEvent> | undefined = undefined;
  public todos: Array<SchedulerEvent> | undefined = undefined;
  public draftHealthRecords: Array<any> | undefined = undefined;
  public outerPanelSizes: number[] = [50, 50];
  public topPanelSizes: number[] = [50, 50];
  public bottomPanelSizes: number[] = [50, 50];
  @Input() userId: string | undefined = undefined;
  @Input() eventDate: Date | undefined = undefined;
  @Output() rowSelected = new EventEmitter<SchedulerEvent>();
  private eventsChangedSub?: Subscription;

  constructor(
    private scheduleService: ScheduleService,
    private healthRecordService: HealthRecordService,
    private customerService: CustomerService,
    private router: Router,
    private settingService: SettingService
  ) {
    this.appointmentListOptions = {
      showButtons: false,
      showStaff: false,
      showTableHeader: true,
      eventType: 'appointment'
    };
    this.todoListOptions = {
      showButtons: false,
      showStaff: true,
      showTableHeader: true,
      eventType: 'todo'
    };
  }

  ngOnInit(): void {
    // Refresh the lists immediately when an event/todo is saved elsewhere
    // (e.g. the "add todo" dialog), so the new item shows without a page reload.
    this.eventsChangedSub = this.scheduleService.eventsChanged$.subscribe(
      () => {
        this.loadEvents();
      }
    );

    this.settingService.getUserSettings().subscribe((cfg) => {
      if (Array.isArray(cfg?.splitters?.eventsOuter)) {
        this.outerPanelSizes = cfg.splitters.eventsOuter;
      }
      if (Array.isArray(cfg?.splitters?.eventsTop)) {
        this.topPanelSizes = cfg.splitters.eventsTop;
      }
      if (Array.isArray(cfg?.splitters?.eventsBottom)) {
        this.bottomPanelSizes = cfg.splitters.eventsBottom;
      }
    });
  }

  /**
   * Persists the appointments+todos/drafts+expiring group splitter position for the current user.
   */
  onOuterSplitterResizeEnd(event: SplitterResizeEndEvent): void {
    this.settingService
      .saveSplitterSizes('eventsOuter', event.sizes.map(Number))
      .subscribe();
  }

  /**
   * Persists the appointments/todos splitter position for the current user.
   */
  onTopSplitterResizeEnd(event: SplitterResizeEndEvent): void {
    this.settingService
      .saveSplitterSizes('eventsTop', event.sizes.map(Number))
      .subscribe();
  }

  /**
   * Persists the drafts/expiring splitter position for the current user.
   */
  onBottomSplitterResizeEnd(event: SplitterResizeEndEvent): void {
    this.settingService
      .saveSplitterSizes('eventsBottom', event.sizes.map(Number))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.eventsChangedSub?.unsubscribe();
  }

  /**
   * Load events when one of the input parameters change.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.loadEvents();
  }

  /**
   * Retrieves the events of the selected user for a specific day
   * Events can be appointments or todos.
   */
  loadEvents(): void {
    this.appointments = [];
    this.todos = [];
    const searchCriteria = {} as SearchCriteria;
    searchCriteria.userId = this.userId;
    searchCriteria.startDate = this.eventDate;
    searchCriteria.endDate = this.eventDate;
    searchCriteria.itemType = 'appointment';
    this.scheduleService
      .getSchedulerEvents(searchCriteria)
      .subscribe((result) => {
        if (result) {
          this.appointments = result;
        }
      });
    searchCriteria.startDate = null;
    searchCriteria.endDate = null;
    searchCriteria.itemType = null;
    this.scheduleService
      .getUserTodoEvents(searchCriteria)
      .subscribe((result) => {
        if (result) {
          this.todos = result;
        }
      });
    this.healthRecordService.getDraftHealthRecords().subscribe((result) => {
      if (result) {
        this.draftHealthRecords = result;
      }
    });
  }

  /**
   * Navigate to the patient record of the given customer and set them as selected.
   */
  async navigateToCustomer(customerId: string): Promise<void> {
    const customer = await this.customerService.getCustomer(customerId);
    await this.customerService.setSelectedCustomer(customer);
    await this.router.navigate(['customer', 'healthrecord', 'list']);
  }

  /**
   * Remove a draft health record from the home view list without deleting it.
   */
  removeDraftFromList(id: string, event: MouseEvent): void {
    event.stopPropagation();
    this.draftHealthRecords = this.draftHealthRecords?.filter(
      (r) => r.id !== id
    );
  }

  /**
   * Change the selected day and triggers a new event search.
   */
  setDate(direction: string): void {
    let newDate = new Date();
    newDate.setFullYear(
      this.eventDate!.getFullYear(),
      this.eventDate!.getMonth(),
      this.eventDate!.getDate()
    );
    switch (direction) {
      case 'prev':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'next':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'today':
        newDate = new Date();
        break;
    }
    this.eventDate = newDate;
    this.loadEvents();
  }

  /**
   * Selects the row from the appointment list and emits it to the caller.
   */
  onEventSelect(event: SchedulerEvent): void {
    this.rowSelected.emit(event);
  }

  /**
   * Selects the row from the todos list and emits it to the caller.
   */
  onTodoSelect(event: any): void {}
}
